import { MAX_INSTRUMENTS, SFX_CHARACTERS, SFX_CHARACTER_LABELS, type AudioBriefForm } from './audioBrief.js';

/**
 * The Generate source for an audio kind: the closed brief's fields, nothing
 * else. Controlled by the composer, which owns the Generate action, the
 * disclosure and the disabled reason so they sit together next to the button.
 */
export function AudioBriefFields({ value, onChange, disabled, wizardPrefilled }: {
  value: AudioBriefForm;
  onChange: (next: AudioBriefForm) => void;
  disabled: boolean;
  /** True while the first line is still the wizard's direction, verbatim. */
  wizardPrefilled: boolean;
}) {
  const note = wizardPrefilled
    ? <p className="muted" style={{ fontSize: 12, marginTop: 0 }} data-testid="audio-brief-prefilled">
        Prefilled from the direction you gave in the wizard. Edit freely — nothing is sent until you press Generate.
      </p>
    : null;
  if (value.kind === 'music') {
    const f = value.form;
    const set = (patch: Partial<typeof f>) => onChange({ kind: 'music', form: { ...f, ...patch } });
    return <fieldset className="field" disabled={disabled} data-testid="audio-brief-music">
      <legend className="field__label">Music brief — your musical direction, one line each</legend>
      {note}
      <label className="field__label" htmlFor="music-mood">Mood</label>
      <input id="music-mood" className="input" value={f.mood} maxLength={80} placeholder="tense synth chase" onChange={(e) => set({ mood: e.target.value })} />
      <label className="field__label" htmlFor="music-genre">Genre</label>
      <input id="music-genre" className="input" value={f.genre} maxLength={80} placeholder="retro synthwave" onChange={(e) => set({ genre: e.target.value })} />
      <label className="field__label" htmlFor="music-tempo">Tempo (BPM, 40–200)</label>
      <input id="music-tempo" className="input" inputMode="numeric" value={f.tempoBpm} maxLength={3} onChange={(e) => set({ tempoBpm: e.target.value })} />
      <label className="field__label" htmlFor="music-instruments">Instruments (comma-separated, up to {MAX_INSTRUMENTS})</label>
      <input id="music-instruments" className="input" value={f.instrumentation} placeholder="synth bass, drum machine" onChange={(e) => set({ instrumentation: e.target.value })} />
      <label className="field__label" htmlFor="music-feel">Reference feel (optional — a feel, never an artist or a track)</label>
      <input id="music-feel" className="input" value={f.referenceFeel} maxLength={120} placeholder="early arcade attract mode" onChange={(e) => set({ referenceFeel: e.target.value })} />
    </fieldset>;
  }
  const f = value.form;
  const set = (patch: Partial<typeof f>) => onChange({ kind: 'sfx', form: { ...f, ...patch } });
  return <fieldset className="field" disabled={disabled} data-testid="audio-brief-sfx">
    <legend className="field__label">Sound brief — what the sound is, one line</legend>
    {note}
    <label className="field__label" htmlFor="sfx-description">Description</label>
    <input id="sfx-description" className="input" value={f.description} maxLength={80} placeholder="short metallic clang" onChange={(e) => set({ description: e.target.value })} />
    <label className="field__label" htmlFor="sfx-character">Character</label>
    <select id="sfx-character" className="select" value={f.character} onChange={(e) => set({ character: e.target.value as typeof f.character })}>
      {SFX_CHARACTERS.map((c) => <option key={c} value={c}>{SFX_CHARACTER_LABELS[c]}</option>)}
    </select>
  </fieldset>;
}
